import { supabase } from '@/integrations/supabase/client';

export interface BriefBulletsLatinAmerikaTask {
  id: string;
  task_type: string;
  status: string;
  schedule_time: string;
  last_run: string | null;
  next_run: string | null;
  retry_count: number;
  error_message: string | null;
  config: any;
}

export interface TaskLog {
  id: string;
  task_id: string;
  task_type: string;
  status: string;
  message: string;
  metadata: any;
  execution_time_ms: number | null;
  created_at: string;
}

export interface BriefBulletsLatinAmerikaArticle {
  id: string;
  interviewee_name: string;
  answer: string;
  created_at: string;
  timestamp: string;
  publication_status: string;
}

export const briefBulletsLatinAmerikaService = {
  async getTaskStatus(): Promise<BriefBulletsLatinAmerikaTask | null> {
    const { data, error } = await supabase
      .from('scheduled_tasks')
      .select('*')
      .eq('task_type', 'briefr_bullets_latin_amerika')
      .single();

    if (error) {
      console.error('Error fetching Latin Amerika task:', error);
      return null;
    }
    return data;
  },

  async getThisWeekStatus(): Promise<{ published: boolean; article?: BriefBulletsLatinAmerikaArticle }> {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .eq('interviewee_name', 'Kai')
      .ilike('answer', '%Latin-Amerika%')
      .gte('created_at', startOfWeek.toISOString())
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error checking week status:', error);
      return { published: false };
    }

    return {
      published: data.length > 0,
      article: data[0] as any,
    };
  },

  async triggerManualPublish(): Promise<{ success: boolean; articleId?: string; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        'publish-briefr-bullets-latin-amerika',
        {
          body: { manual: true }
        }
      );

      if (error) throw error;

      return {
        success: true,
        articleId: data?.articleId,
      };
    } catch (error) {
      console.error('Error triggering Latin Amerika publication:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  async getPublishHistory(weeks: number = 4): Promise<BriefBulletsLatinAmerikaArticle[]> {
    const weeksAgo = new Date();
    weeksAgo.setDate(weeksAgo.getDate() - (weeks * 7));

    const { data, error } = await supabase
      .from('interviews')
      .select('*')
      .eq('interviewee_name', 'Kai')
      .ilike('answer', '%Latin-Amerika%')
      .gte('created_at', weeksAgo.toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching Latin Amerika history:', error);
      return [];
    }

    return data as any[];
  },

  async getTaskLogs(limit: number = 10): Promise<TaskLog[]> {
    const { data, error } = await supabase
      .from('task_logs')
      .select('*')
      .eq('task_type', 'briefr_bullets_latin_amerika')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching Latin Amerika logs:', error);
      return [];
    }

    return data;
  },

  async toggleAutomaticPublishing(enable: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scheduled_tasks')
        .update({
          status: enable ? 'active' : 'paused',
        })
        .eq('task_type', 'briefr_bullets_latin_amerika');

      if (error) {
        console.error('Error toggling automatic publishing:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error toggling automatic publishing:', error);
      return false;
    }
  },

  async deleteThisWeekArticle(): Promise<{ success: boolean; error?: string }> {
    try {
      const weekStatus = await this.getThisWeekStatus();
      
      if (!weekStatus.published || !weekStatus.article) {
        return { success: false, error: 'No article found for this week' };
      }

      // First update the status
      const { error: updateError } = await supabase
        .from('interviews')
        .update({ publication_status: 'deleted' })
        .eq('id', weekStatus.article.id);

      if (updateError) throw updateError;

      // Then delete the article
      const { error: deleteError } = await supabase
        .from('interviews')
        .delete()
        .eq('id', weekStatus.article.id);

      if (deleteError) throw deleteError;

      return { success: true };
    } catch (error) {
      console.error('Error deleting Latin Amerika article:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  async updatePhotoUrl(photoUrl: string) {
    const { error } = await supabase.rpc('update_briefr_bullets_latin_amerika_config', {
      new_photo_url: photoUrl
    });

    if (error) throw error;
  },

  // Legacy methods for backward compatibility
  async getScheduledTask() {
    return this.getTaskStatus();
  },

  async updateScheduledTask(updates: {
    schedule_time?: string;
    status?: string;
    next_run?: string;
  }) {
    const { data, error } = await supabase
      .from('scheduled_tasks')
      .update(updates)
      .eq('task_type', 'briefr_bullets_latin_amerika')
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async triggerPublication() {
    return this.triggerManualPublish();
  }
};